/**
 * useActiveCarePlan — v135.1.0
 * 
 * Single source of truth for encounter-derived care plan data.
 * Resolves active encounter from encounters array, merges care_plan
 * with flat followUp fallback for backward compatibility.
 * 
 * Used by: DeliverScreen, CaseHome, DiagnoseScreen
 * 
 * Priority: encounter care_plan > flat followUp > defaults
 */

import { unwrapAvs } from '../utils/avsText' // PROD-134 gamma
import { useMemo } from 'react'
import { activeEncounter } from '../utils/caseEncounters'
import { caseHasSignedVisit, planFollowUp } from '../utils/signedPlan' // PROD-1079: no draft plan before the first signing
import { asText, asFirst } from '../utils/asText' // PROD-1156: a plan text field is text, never an object

/**
 * v223.2.2 — Maps an MD Messenger follow-up urgency to a human-readable
 * timing phrase and an exact scheduling window (in days).
 *
 * The MD Messenger "convert to follow-up" flow stamps care_plan.urgency
 * ('asap' | 'within_week') + care_plan.windowDays (3 | 7) on the encounter.
 * It does NOT set a free-text timeframe, so any consumer that reads timeframe
 * would otherwise fall back to a generic default. Centralize the mapping here
 * so every screen renders the same thing.
 *
 * Returns null when there is no recognized urgency.
 */
export function urgencyToTiming(urgency) {
  if (urgency === 'asap') return { timeframe: '3 days', windowDays: 3 }
  if (urgency === 'within_week') return { timeframe: '1 week', windowDays: 7 }
  return null
}

/**
 * Derives the active encounter object from case data.
 * Exported separately so non-hook contexts (store computed) can use the same logic.
 */
export function resolveActiveEncounter(currentCase) {
  // ★★★ v242.170.0 — THIS FUNCTION HAD THE RULE RIGHT AND NOTHING ELSE USED IT.
  //   Pointer first, encounter-number order second, newest row as the fallback —
  //   the MD panel's rule, written here, while the Deliver screen resolved the
  //   same question three other ways one directory over. The body now lives in
  //   utils/caseEncounters.js so there is ONE answer; this stays as the name the
  //   care-plan hook has always called, and its behaviour is unchanged.
  return activeEncounter(currentCase)
}

/**
 * Resolves canonical care plan from active encounter + flat followUp fallback.
 * Returns a normalized object with all care plan fields.
 */
export function resolveCarePlan(currentCase) {
  if (!currentCase) return null
  
  const activeEnc = resolveActiveEncounter(currentCase)
  // ★ PROD-1079: md autosaves its DRAFT plan into cases.follow_up (utils/signedPlan.js). Until
  //   the case has a signed visit, the column's plan keys are dropped and the unsigned row
  //   care_plan (a copy of the column) is not read. Once a visit is signed, both are read
  //   exactly as before 1079 (fix 2: the row care_plan of a follow-up lacks signed buckets).
  const encCarePlan = caseHasSignedVisit(currentCase) ? (activeEnc?.care_plan || activeEnc?.carePlan || {}) : {}
  const followUp = planFollowUp(currentCase)
  const deliver = currentCase.deliver || {}

  // v223.2.2: Messenger-converted follow-up urgency. care_plan.urgency +
  // care_plan.windowDays are stamped by the MD Messenger "convert to follow-up"
  // flow (and care_plan.spawnedFromMessenger / updatedFromMessenger mark it).
  const messengerUrgency = encCarePlan.urgency || followUp.urgency || null
  const messengerWindowDays = encCarePlan.windowDays || followUp.windowDays || null
  const spawnedFromMessenger = !!(
    encCarePlan.spawnedFromMessenger || encCarePlan.updatedFromMessenger || followUp.spawnedFromMessenger
  )
  const urgencyTiming = urgencyToTiming(messengerUrgency)

  // ★ PROD-1156: the TEXT fields of the plan pass through asText (utils/asText.js), so an object
  //   in one of them reaches no screen as a React child. A string is unchanged; a falsy value
  //   stays as it was (callers test it with ||); an object reads as its readable field or ''.
  //   LOW-1: each candidate is cleaned BEFORE the || picks, so an object in the first slot falls
  //   through to the next real string instead of hiding it.
  const planText = (...vs) => { const s = asFirst(...vs); if (s) return s; const last = vs[vs.length - 1]; return last ? '' : last }

  // Merge: encounter care_plan takes priority over flat followUp
  const merged = {
    type: planText(encCarePlan.followUpPlan, encCarePlan.disposition, followUp.type),
    // v223.2.2: fall back to the urgency-derived timing so messenger-spawned
    // follow-ups (which carry no free-text timeframe) still render a window.
    timeframe: planText(encCarePlan.timeframe, encCarePlan.followUpTiming, followUp.timeframe, urgencyTiming?.timeframe),
    visitType: planText(encCarePlan.visitType, followUp.visitType) || 'in-person',
    observationNotes: planText(encCarePlan.observationNotes, followUp.observationNotes),
    observationParameters: encCarePlan.parameters || followUp.parameters || null, // v135.3.0 PA-C2: structured parameters
    escalationCriteria: planText(encCarePlan.escalationCriteria, followUp.escalationCriteria),
    observationInterval: planText(encCarePlan.observationInterval, followUp.observationInterval),
    checkInSchedule: planText(encCarePlan.checkInSchedule, followUp.checkInSchedule),
    observationStartDate: encCarePlan.observationStartDate,
    // ★ D332(m): the check-up due date is a SERVER-OWNED field now — stamped at
    //   finalize and advanced whenever an answer lands. Exposed so DeliverScreen
    //   reads it instead of deriving a second answer to the same question.
    nextCheckUpDue: encCarePlan.nextCheckUpDue,
    observationEndDate: encCarePlan.observationEndDate,
    positiveOutcome: encCarePlan.positiveOutcome || followUp.positiveOutcome,
    negativeOutcome: encCarePlan.negativeOutcome || followUp.negativeOutcome,
    signOutNotes: planText(encCarePlan.signOutNotes, followUp.signOutNotes),
    reasonsToReturn: planText(encCarePlan.reasonsToReturn, followUp.reasonsToReturn),
    referralSpecialty: planText(encCarePlan.referralSpecialty, followUp.referralSpecialty),
    referralNotes: planText(encCarePlan.referralNotes, followUp.referralNotes),
  }

  const requirements = followUp.requirements || {}
  const ordersPlaced = encCarePlan.ordersPlaced || followUp.ordersPlaced || {}

  return {
    // Plan type
    followUpPlan: merged.type,
    // Orders (v221.1.2: 8 categories — dme + diagnosticTests added)
    careOrders: ordersPlaced.careOrders || [],
    medications: ordersPlaced.medications || [],
    labs: ordersPlaced.labs || [],
    imaging: ordersPlaced.imaging || ordersPlaced.radiology || [],
    // v221.1.2: diagnosticTests is the canonical bucket; falls back to legacy
    // additionalOrders for cases persisted before v221.1.0.
    diagnosticTests: ordersPlaced.diagnosticTests || ordersPlaced.additionalOrders || [],
    dme: ordersPlaced.dme || [],
    // s128: scales the clinician confirmed (Clinical Panel 243.347.0) — asked again in the follow-up report
    scales: Array.isArray(ordersPlaced.scales) ? ordersPlaced.scales : (Array.isArray(encCarePlan.scales) ? encCarePlan.scales : []),
    // Legacy back-compat — kept so any code path that still reads
    // additionalOrders directly continues to work.
    additionalOrders: ordersPlaced.additionalOrders || ordersPlaced.diagnosticTests || [],
    procedures: ordersPlaced.procedures || [],
    referrals: ordersPlaced.referrals || [],
    customOrders: ordersPlaced.custom || [],
    dueDates: ordersPlaced.dueDates || {},
    customDates: ordersPlaced.customDates || {},
    // Timing & scheduling
    followUpTiming: merged.timeframe,
    visitType: merged.visitType,
    // v223.2.2: Messenger-driven urgency surfaced for all consumers.
    //   followUpUrgency:   'asap' | 'within_week' | null
    //   followUpWindowDays: exact scheduling window (3 | 7) — use directly
    //                       instead of parsing the timeframe string.
    //   spawnedFromMessenger: true when this follow-up came from a converted thread
    followUpUrgency: messengerUrgency,
    followUpWindowDays: messengerWindowDays || urgencyTiming?.windowDays || null,
    spawnedFromMessenger,
    // Observation
    observationNotes: merged.observationNotes,
    observationParameters: merged.observationParameters,
    escalationCriteria: merged.escalationCriteria,
    observationInterval: merged.observationInterval,
    checkInSchedule: merged.checkInSchedule,
    observationStartDate: merged.observationStartDate,
    observationEndDate: merged.observationEndDate,
    positiveOutcome: merged.positiveOutcome,
    negativeOutcome: merged.negativeOutcome,
    // Complete / discharge
    signOutNotes: merged.signOutNotes,
    reasonsToReturn: merged.reasonsToReturn,
    // Referral
    referralSpecialty: merged.referralSpecialty,
    referralNotes: merged.referralNotes,
    otherRequirements: (requirements.other && requirements.otherText) ? asText(requirements.otherText) : '',
    // Encounter referrals from referrals table (separate from order-based referrals)
    encounterReferrals: currentCase.referrals || [],
    // Active encounter metadata
    activeEncounter: activeEnc,
    encounterCategory: activeEnc?.category || activeEnc?.appointment_category,
    // v135.2.0 PA-C3: Encounter-level document fields (not in care_plan, on the encounter itself)
    encounterAvs: unwrapAvs(activeEnc?.avs || activeEnc?.after_visit_summary) || null,
    // PROD-1079: encounterConsultNote removed. It had no reader and no hold / signed check
    //   (utils/signedVisit.js is the one reader of a visit's note).
    encounterFinalized: activeEnc?.finalized || false,
    encounterFinalizedAt: activeEnc?.finalized_at || activeEnc?.finalizedAt || null,
    // Completion tracking (from deliver state) — v221.1.2: dme + diagnosticTests added
    careOrdersComplete: deliver.careOrdersComplete || false,
    medicationSurveyComplete: deliver.medicationSurveyComplete || false,
    labsComplete: deliver.labsComplete || false,
    imagingComplete: deliver.radiologyComplete || deliver.imagingComplete || false,
    referralsComplete: deliver.referralsComplete || false,
    diagnosticTestsComplete: deliver.diagnosticTestsComplete || deliver.additionalOrdersComplete || false, // v221.1.2 with back-compat
    dmeComplete: deliver.dmeComplete || false,                                                            // v221.1.2
    additionalOrdersComplete: deliver.additionalOrdersComplete || false, // legacy
    proceduresComplete: deliver.proceduresComplete || false,
    customOrdersComplete: deliver.customOrdersComplete || false,
    otherComplete: deliver.otherComplete || false,
    // v165.0.0: Facility & auth — patient-set (deliver) takes priority over MD-set (ordersPlaced)
    imagingFacility: deliver.imagingFacility || ordersPlaced.imagingFacility || null,
    imagingAuth: deliver.imagingAuth || ordersPlaced.imagingAuth || '',
    imagingStatus: deliver.imagingStatus || (deliver.radiologyComplete || deliver.imagingComplete ? 'completed' : 'pending'),
    imagingPrep: deliver.imagingPrep || null,
    imagingPrepNotes: asText(ordersPlaced.imagingPrepNotes),
    imagingBookingUrl: deliver.imagingBookingUrl || '',
    labsFacility: deliver.labsFacility || ordersPlaced.labsFacility || null,
    labsAuth: deliver.labsAuth || ordersPlaced.labsAuth || '',
    labsStatus: deliver.labsStatus || (deliver.labsComplete ? 'completed' : 'pending'),
    labsPrep: deliver.labsPrep || null,
    labsPrepNotes: asText(ordersPlaced.labsPrepNotes),
    labsBookingUrl: deliver.labsBookingUrl || '',
  }
}

/**
 * React hook — memoized care plan resolution.
 * Drop-in replacement for the inline useMemo in DeliverScreen.
 */
export default function useActiveCarePlan(currentCase) {
  return useMemo(() => resolveCarePlan(currentCase), [currentCase])
}
